import { Suspense, lazy } from "react";
import {
  Navigate,
  Outlet,
  createBrowserRouter,
  type RouteObject,
} from "react-router-dom";
import { RootLayout } from "./components/layouts/RootLayout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { IndexRedirect } from "./components/IndexRedirect";
import { Skeleton } from "./components/ui/skeleton";
import { Login } from "./pages/Login";
import { Signup } from "./pages/Signup";
import { SignupFinalize } from "./pages/SignupFinalize";
import { Team } from "./pages/Team";
import { AcceptInvitation } from "./pages/AcceptInvitation";
import { CompleteSetup } from "./pages/CompleteSetup";
import { ChangePassword } from "./pages/ChangePassword";
import { OAuthConsent } from "./pages/OAuthConsent";
import { Settings } from "./pages/Settings";
import { Billing } from "./pages/Billing";
import { NotFound } from "./pages/NotFound";
import { isAlertingEnabled, isBillingEnabled } from "./lib/features";

const LazyObservability = lazy(() =>
  import("@/pages/Observability").then((m) => ({ default: m.Observability })),
);

// Lazy like Observability so the alerting pages' SDK usage stays out of the
// main chunk.
const LazyAlertRules = lazy(() =>
  import("@/pages/AlertRules").then((m) => ({ default: m.AlertRules })),
);
const LazyAlertRuleEditor = lazy(() =>
  import("@/pages/AlertRuleEditor").then((m) => ({
    default: m.AlertRuleEditor,
  })),
);
const LazyAlertIncident = lazy(() =>
  import("@/pages/AlertIncident").then((m) => ({ default: m.AlertIncident })),
);
const LazyAlertTargets = lazy(() =>
  import("@/pages/AlertTargets").then((m) => ({ default: m.AlertTargets })),
);

const pageFallback = (
  <div className="p-6 space-y-4">
    <Skeleton className="h-8 w-48" />
    <Skeleton className="h-64 w-full" />
  </div>
);

/**
 * Built on call rather than at module load so the visibility flags it
 * reads can be varied per test without re-importing the module graph.
 */
export function createRoutes(): RouteObject[] {
  return [
    {
      path: "/login",
      element: <Login />,
    },
    {
      path: "/signup",
      element: <Signup />,
    },
    {
      path: "/signup/finalize",
      element: <SignupFinalize />,
    },
    {
      path: "/accept-invitation",
      element: <AcceptInvitation />,
    },
    {
      path: "/complete-setup",
      element: <CompleteSetup />,
    },
    {
      // Standalone like the two above: ProtectedRoute would send a
      // pending-workspace session to /complete-setup before the consent
      // page could explain why it can't approve (ADR-058).
      path: "/oauth/consent",
      element: <OAuthConsent />,
    },
    {
      path: "/change-password",
      element: (
        <ProtectedRoute>
          <ChangePassword />
        </ProtectedRoute>
      ),
    },
    {
      path: "/",
      element: (
        <ProtectedRoute>
          <RootLayout />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: <IndexRedirect />,
        },
        {
          path: "team",
          element: <Team />,
        },
        // Billing ships dark: with the flag off no route exists at all, so
        // `/billing` falls through to NotFound instead of rendering a page
        // against an environment that cannot serve it.
        ...(isBillingEnabled()
          ? [{ path: "billing", element: <Billing /> }]
          : []),
        {
          path: "settings",
          element: <Settings />,
        },
        {
          path: "observability",
          element: (
            <Suspense fallback={pageFallback}>
              <LazyObservability />
            </Suspense>
          ),
        },
        // Alerting ships dark like billing: with the flag off `/alerts/*`
        // falls through to NotFound.
        ...(isAlertingEnabled()
          ? [
              {
                path: "alerts",
                element: (
                  <Suspense fallback={pageFallback}>
                    <Outlet />
                  </Suspense>
                ),
                children: [
                  {
                    index: true,
                    element: <Navigate to="/alerts/rules" replace />,
                  },
                  { path: "rules", element: <LazyAlertRules /> },
                  { path: "rules/new", element: <LazyAlertRuleEditor /> },
                  { path: "rules/:id", element: <LazyAlertRuleEditor /> },
                  {
                    path: "rules/:ruleId/incidents/:eventId",
                    element: <LazyAlertIncident />,
                  },
                  { path: "targets", element: <LazyAlertTargets /> },
                ],
              },
            ]
          : []),
        {
          path: "*",
          element: <NotFound />,
        },
      ],
    },
  ];
}

export const routes: RouteObject[] = createRoutes();

export const router = createBrowserRouter(routes);
